import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from 'react-hot-toast';
import GridShape from "../../components/common/GridShape";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../../components/form/input/Checkbox";
import PageMeta from "../../components/common/PageMeta";
import { authService } from "../../services/authService";
import Button from "../../components/ui/button/Button";

export default function SignUp() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invitationCode, setInvitationCode] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isChecked) {
      toast.error('Debes aceptar los términos y condiciones', {
        icon: '⚠️',
      });
      return;
    }

    if (!firstName || !lastName || !email || !password || !invitationCode) {
      toast.error('Por favor completa todos los campos', {
        icon: '⚠️',
      });
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading('Creando tu cuenta...');

    try {
      const name = `${firstName} ${lastName}`;
      await authService.register({ email, password, name, invitationCode });

      toast.dismiss(loadingToast);
      
      await toast.promise(
        new Promise((resolve) => setTimeout(resolve, 1000)),
        {
          loading: 'Finalizando registro...',
          success: '¡Cuenta creada exitosamente!',
          error: 'Error al crear la cuenta',
        },
        {
          success: {
            duration: 2000,
            icon: '✅',
          },
        }
      );

      setTimeout(() => {
        navigate("/login");
      }, 500);

    } catch (error) {
      toast.dismiss(loadingToast);
      
      let errorMessage = 'Error al crear la cuenta';
      if (error instanceof Error) {
        if (error.message.includes('email already exists')) {
          errorMessage = 'El correo electrónico ya está en uso.';
        } else if (error.message.includes('network error')) {
          errorMessage = 'Error de red. Por favor, verifica tu conexión a Internet.';
        } else if (error.message.includes('Invalid invitation code')) {
          errorMessage = 'El código de invitación no es válido.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage, {
        duration: 3000,
        icon: '❌',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Registrarse | Destiny Caribbean Tours"
        description="Registro de nuevo usuario en el panel de administración de Destiny Caribbean Tours"
      />
      <div className="relative flex w-full h-screen overflow-hidden bg-white z-1 dark:bg-gray-900">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="w-full max-w-md pt-5 mx-auto sm:py-10"></div>
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div className="mb-5 sm:mb-8">
              <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                Registrarse
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ¡Ingresa tus datos para crear una cuenta!
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <Label>
                      Nombre<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="fname"
                      name="fname"
                      placeholder="Ingresa tu nombre"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label>
                      Apellido<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="lname"
                      name="lname"
                      placeholder="Ingresa tu apellido"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>
                    Correo electrónico<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Ingresa tu correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label>
                    Contraseña<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      placeholder="Ingresa tu contraseña"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <Label>
                    Código de invitación<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="invitationCode"
                    name="invitationCode"
                    placeholder="Ingresa tu código de invitación"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    className="w-5 h-5"
                    checked={isChecked}
                    onChange={setIsChecked}
                  />
                  <p className="inline-block font-normal text-gray-500 dark:text-gray-400">
                    Al crear una cuenta, aceptas nuestros{" "}
                    <span className="text-gray-800 dark:text-white/90">
                      términos y condiciones
                    </span>{" "}
                    y nuestra{" "}
                    <span className="text-gray-800 dark:text-white">
                      política de privacidad
                    </span>
                  </p>
                </div>
                <div>
                  <Button 
                    type="submit"
                    disabled={isLoading}
                    className="w-full"
                  >
                    {isLoading ? "Creando cuenta..." : "Registrarse"}
                  </Button>
                </div>
              </div>
            </form>
            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                ¿Ya tienes una cuenta?{" "}
                <Link
                  to="/login"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Iniciar sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          <GridShape />
          <div className="flex flex-col items-center max-w-xs">
            <Link to="/" className="block mb-4">
              <img src="./images/logo/auth-logo.svg" alt="Destiny Caribbean Tours" />
            </Link>
            <p className="text-center text-gray-400 dark:text-white/60">
              Sistema de gestión de tours y reservas
            </p>
          </div>
        </div>
      </div>
    </>
  );
}